<script setup>
import { useHeroAltLogo } from '@/composables/useHeroAltLogo';

const props = defineProps({
    headline: {
        type: String,
        required: true,
    },
    subHead: {
        type: String,
        required: true,
    },
    includeNewsletter: {
        type: Boolean,
        required: false,
    },
});

// Dark "alt" header logo — only when this block is index 0 (the page hero).
const rootRef = ref(null);
useHeroAltLogo(rootRef);

const { data, error } = useLazyAsyncData('featured-data', () => $fetch('/api/v1/featured-posts'));

const { $bus } = useNuxtApp();
const handleClick = (payload) => {
    $bus.emit('trigger-blog-card-click', payload);
};
</script>

<template>
    <section v-if="data && data.length"
        ref="rootRef"
        class="block-blog-hero treat-as-hero overflow-hidden text-[var(--magenta)] bg-pink pt-[97px] pb-[var(--box-padding-tb-m)] min-[1024px]:pt-[150px] min-[1024px]:mb-[60px]"
        tabindex="-1">
        <div class="px-(--box-padding-lr-m) lg:px-(--box-padding-lr) z-2 overflow-hidden">
            <ProjectBreadcrumbs light />
            <div class="flex items-center mb-[2.25rem] min-[1024px]:mb-[3.25rem] max-[1024px]:display-block">
                <MessHtml tag="h1" class="headline h1 mb-0 font-hubspot-serif font-normal leading-[3.5rem] tracking-normal min-[1024px]:leading-[5.625rem]" :html="headline" />
            </div>
            <div class="flex max-[1024px]:flex-col">
                <FragmentsCardsBlogCard class="featured-post w-1/2 max-[1024px]:w-full max-[1024px]:mb-[1.25rem]" :link="data[0].data.url"
                    :altText="data[0].data.imageAltText" :headline="data[0].data.title"
                    :cardImage="data[0].data.image" :category="data[0].data.category"
                    :date="data[0].data.datePublished" :excerpt="data[0].data.excerpt" :tags="data[0].data.tags"
                    tagColor="magenta" size="large" @clicked="handleClick" />
                <ul class="posts-container w-[45.6%] ml-[4%] max-[1024px]:ml-0 max-[1024px]:w-full" v-if="data">
                    <li v-for="(card, index) in data.slice(1, 5)" :key="index" class="post">
                        <FragmentsCardsBlogCard :link="card.data.url" :headline="card.data.title"
                            :cardImage="card.data.image" :altText="card.data.imageAltText"
                            :category="card.data.category" :date="card.data.datePublished" :excerpt="card.data.excerpt"
                            :tags="card.data.tags" tagColor="magenta" size="small" @clicked="handleClick" />
                    </li>
                </ul>
            </div>
        </div>
    </section>
</template>

<style scoped>
/* [url()] — background-image asset reference */
/* [child] — reaching into child component h1 line-height */
.block-blog-hero :deep(h1) {
    line-height: 1.2315;
}

/* [child] — featured post child .date letter-spacing */
.featured-post :deep(.date) {
    letter-spacing: 0.0375rem;
}

/* [child] — featured post child h3.headline: 30px mobile, 60px desktop per 2026 Figma */
.block-blog-hero .featured-post :deep(h3.headline) {
    font-size: calc(30 / var(--base-fs) * 1rem);
    line-height: 1.1;
    letter-spacing: 0;
}

@media (min-width: 768px) {
    .block-blog-hero .featured-post :deep(h3.headline) {
        font-size: calc(40 / var(--base-fs) * 1rem);
    }
}

@media (min-width: 1024px) {
    .block-blog-hero .featured-post :deep(h3.headline) {
        font-size: calc(60 / var(--base-fs) * 1rem);
    }
}

/* [child] — posts-container child h3 typography */
.posts-container .post :deep(h3) {
    font-size: 1.45rem;
    letter-spacing: 0;
    line-height: 1.925rem;
}

/* [child] — posts-container child .date letter-spacing */
.posts-container .post :deep(.date) {
    letter-spacing: 0.0375rem;
}

/* [state] — post spacing except last */
.posts-container .post:not(:last-of-type) {
    margin-bottom: 2rem;
}

/* [child] — newsletter signup reduced padding at mobile */
@media (max-width: 1024px) {
    .block-blog-hero :deep(.block-newsletter-signup.reduced-padding) {
        font-size: 11px;
    }
    .block-blog-hero :deep(.block-newsletter-signup.reduced-padding .h6) {
        font-size: 10px;
    }
}
</style>
